import snakecaseKeys from 'snakecase-keys';

import {
  CamelCaseKeys, NameDisplay, UserCommunityExpertise, UserEthnicity, UserFaith, UserGender,
  UserLanguage, UserModality, UserQualification, UserSpecialty, UserSupportedAgeGroup,
  UserTherapeuticStyle, UserTreatmentApproach
} from '@uplift/types';

import { restClient } from '../../client';
import {
  InsuranceCompany,
  License, Practice, PracticeLocation, ProviderDetail, ProviderInsurance, UpliftLicenseType, User, UserContact,
  UserProfile,
  Asset,
  ProviderInsuranceTin,
  ProviderInsuranceRosterStatusEnum,
} from '../../resources';

type SavePracticeLocationInput =
    & Omit<PracticeLocation, 'id' | 'practiceId' | 'createdAt' | 'updatedAt' | 'deletedAt'>
    & { replaceLocationId?: string };


type GetProviderPracticeOutput = { data: { practice: Practice; locations: PracticeLocation[] } };
type PracticeLocationOutput = { data: PracticeLocation };

export type ProviderSchedule = CamelCaseKeys<{
  user_id: string;
  ideal_caseload?: number;
  availability_total_hours?: number;
}>;

export const getProviderSchedule = (providerId: string) => restClient.request<{
  data: ProviderSchedule;
}>(`/api/v1/provider/${providerId}/schedule`);

export const getBatchProviderSchedules = (providerIds: string[]) => restClient.request<{
  data: ProviderSchedule[];
}>('/api/v1/admin/provider_schedules', {
  method: 'POST',
  data: {
    provider_ids: providerIds,
  }
});


export const updateProviderSchedule = (providerId: string, data: { idealCaseload: number }) => restClient.request<null>(`/api/v1/provider/${providerId}/schedule`, {
  method: 'PUT',
  data: snakecaseKeys(data),
});


export const getProviderPractice = async (data: { providerId?: string }) => {
  return restClient.request<GetProviderPracticeOutput>('/api/v1/provider/practice', { method: 'GET', params: snakecaseKeys(data) });
};

export const savePracticeLocation = async (data: SavePracticeLocationInput) => {
  return restClient.request<PracticeLocationOutput>('/api/v1/provider/practice/location', { method: 'POST', data: snakecaseKeys(data) });
};

export const deletePracticeLocation = async (locationId: string) => {
  return restClient.request(`/api/v1/provider/practice/location/${locationId}`, { method: 'DELETE' });
};

export type GetProviderDetailsResponse = {
  data: ProviderDetail;
};

export const getProviderDetails = async (providerId: string) =>
  restClient.request<GetProviderDetailsResponse>(`/api/v1/provider/${providerId}/details`);

export type PatchProviderDetailsInput = Partial<ProviderDetail>;

export const patchProviderDetails = async (providerId: string, data: PatchProviderDetailsInput) =>
  restClient.request<{data: ProviderDetail}>(
    `/api/v1/provider/${providerId}/details`,
    {
      method: 'PATCH',
      data: snakecaseKeys(data)
    }
  );


export const deleteProviderLicense = async (providerId: string, licenseId: string) =>
  restClient.request(
    `/api/v1/provider/${providerId}/license/${licenseId}`,
    {
      method: 'DELETE',
    }
  );


export type LicenseInput = {
  userId?: string;
  number: string;
  stateId?: string;
  expiration: string;
  mentalHealthRoleId: string;
  active?: boolean;
};


export const createLicense = async (providerId: string, data: Partial<LicenseInput>) =>
  restClient.request<{data: License}>(
    `/api/v1/provider/${providerId}/license`,
    {
      method: 'POST',
      data: snakecaseKeys(data)
    }
  );


export const patchLicense = async (providerId: string, licenseId: string, data: Partial<LicenseInput>) =>
  restClient.request<{data: License}>(
    `/api/v1/provider/${providerId}/license/${licenseId}`,
    {
      method: 'PATCH',
      data: snakecaseKeys(data)
    }
  );


export type ProviderInsuranceInput = {
  insuranceCompanyId: string;
  active: boolean;
  effectiveFrom: string | null;
  rosterStatus: ProviderInsuranceRosterStatusEnum;
};


export const createProviderInsurance = async (providerId: string, data: ProviderInsuranceInput) =>
  restClient.request<{data: ProviderInsurance}>(
    `/api/v1/provider/${providerId}/insurance`,
    {
      method: 'POST',
      data: snakecaseKeys(data)
    }
  );

export const patchProviderInsuranceRoster = async (providerId: string, insuranceId: string, data: Partial<ProviderInsuranceInput>) =>
  restClient.request<{ data: ProviderInsurance }>(
    `/api/v1/provider/${providerId}/insurance/${insuranceId}/roster`,
    {
      method: 'PATCH',
      data: snakecaseKeys(data)
    }
  );

export const deleteProviderInsurance = async (providerId: string, insuranceId: string) =>
  restClient.request(
    `/api/v1/provider/${providerId}/insurance/${insuranceId}`,
    {
      method: 'DELETE',
    }
  );


export const getProvidersUpliftLicensesTypes = async (providerIds: string[]) =>
  restClient.rawRequest<{ data: Record<string, UpliftLicenseType[]> }>(
    '/api/v1/provider/get_providers_licenses_types',
    {
      method: 'POST',
      data: {
        provider_ids: providerIds,
      },
    }
  );

export type ProviderLicenseTypesResponse = {
  data: string[];
};

export const getProviderLicenseTypes = async (providerId: string) =>
  restClient.request<ProviderLicenseTypesResponse>(`/api/v1/provider/${providerId}/license_types`,
    {
      method: 'GET',
    }
  );

export type ProviderIsLicensedInStateResponse = {
  error?: string;
  data?: boolean;
};

export const getProviderIsLicensedInState = (providerId: string, stateId: string) =>
  restClient.request<ProviderIsLicensedInStateResponse>(`/api/v1/provider/${providerId}/licensed/${stateId}`);

export type MatchingProfileData = {
  user: User;
  profile: UserProfile;
  avatarAsset?: Asset;
  practice: Practice;
  locations: PracticeLocation[];
  specialties: UserSpecialty[];
  licenses: CamelCaseKeys<License[]>;
  modalities: UserModality[];
  supportedAgeGroups: UserSupportedAgeGroup[];
  treatmentApproaches: UserTreatmentApproach[];
  communityExpertise: {
    ethnicities: UserEthnicity[];
    faiths: UserFaith[];
    general: UserCommunityExpertise[];
  };
  ethnicities: UserEthnicity[];
  faiths: UserFaith[];
  languages: UserLanguage[];
  therapeuticStyles: UserTherapeuticStyle[];
  supportedInsuranceCompanies: InsuranceCompany[];
  genders: UserGender[];
  qualification: UserQualification;
  nameDisplay: NameDisplay;
  npi: string;

  nextAvailableDate?: string;
};

export type PublicProviderProfileResponse = {
  data: {
    publicProfile: {
      userId: string;
      url: string;
      id: string;
      stateShortName: string;
    };
  } & MatchingProfileData;
};

export const getProviderProfileFromUrl = (urlData: string) =>
  restClient.request<PublicProviderProfileResponse>('/api/v1/provider/for_public_url', {
    method: 'POST',
    data: {
      public_url: urlData
    }
  });


export type GetProviderProfileResponse = {
  data: {
    contacts: UserContact[];
  } & MatchingProfileData;
};

export const getProviderProfile = (providerId: string) => restClient.request<GetProviderProfileResponse>(`/api/v1/provider/${providerId}/matching_profile`);

export type GetProviderPublicProfileResponse = {
  data: MatchingProfileData;
};

export const getProviderPublicProfile = (providerId: string) => restClient.request<GetProviderPublicProfileResponse>(`/api/v1/provider/${providerId}/public_matching_profile`);

export type ProviderAvatarAssetResponse = {
  data: {
    avatarAsset: Asset;
    avatarAssetOriginal: Asset;
  };
};

export const getProviderAvatarAsset = (providerId: string) => restClient.request<ProviderAvatarAssetResponse>(`/api/v1/provider/${providerId}/avatar_asset`);

export type ProviderInsurancesResponse = {
  data: ProviderInsurance[];
};

export const getProviderInsurances = async (providerId: string, stateId?: string) =>
  restClient.request<ProviderInsurancesResponse>(`/api/v1/provider/${providerId}/insurance`, {
    method: 'GET',
    params: {
      state_id: stateId
    }
  });


export type ProviderInsuranceTinResponse = {
  data: ProviderInsuranceTin[];
};

export const getProviderInsuranceTin = async () =>
  restClient.request<ProviderInsuranceTinResponse>('/api/v1/provider_insurance/tin', {
    method: 'GET',
  });

export const getProviderInsuranceTinsForState = async () =>
  restClient.rawRequest<{
    data: Record<string, string[]>;
  }>('/api/v1/provider_insurance/tin_for_states', {
    method: 'GET',
  });

export type PatchProviderPracticeInput = {
  name: string;
};

export const patchProviderPractice = async (providerId: string, data: PatchProviderPracticeInput) =>
  restClient.request(`/api/v1/provider/${providerId}/practice`, { method: 'PATCH', data });

export type UpdateProviderTreatmentApproachesInput = {
  treatment_approach_ids: string[];
};

export const updateProviderTreatmentApproaches = async (data: UpdateProviderTreatmentApproachesInput) =>
  restClient.request('/api/v1/provider/matching_profile/treatment_approaches', { method: 'POST', data });

export type UpdateProviderSupportedAgeGroupsInput = {
  age_group_ids: string[];
};

export const updateProviderSupportedAgeGroups = async (data: UpdateProviderSupportedAgeGroupsInput) =>
  restClient.request('/api/v1/provider/matching_profile/supported_age_groups', { method: 'POST', data });

export type UpdateProviderModalitiesInput = {
  modality_ids: string[];
};

export const updateProviderModalities = async (data: UpdateProviderModalitiesInput) =>
  restClient.request('/api/v1/provider/matching_profile/modalities', { method: 'POST', data });

export type UpdateProviderSpecialtiesInput = {
  specialty_ids: string[];
};

export const updateProviderSpecialties = async (data: UpdateProviderSpecialtiesInput) =>
  restClient.request('/api/v1/provider/matching_profile/specialties', { method: 'POST', data });

export type UpdateProviderFaithExpertiesInput = {
  faith_ids: string[];
  faiths_free_text?: string[];
};

// type: Personal
export const updateProviderFaiths = async (data: UpdateProviderFaithExpertiesInput) =>
  restClient.request('/api/v1/provider/matching_profile/faiths', { method: 'POST', data });

export type UpdateProviderEthnicityExpertiesInput = {
  ethnicity_ids: string[];
  ethnicities_free_text?: string[];
};

// type: Personal
export const updateProviderEthnicities = async (data: UpdateProviderEthnicityExpertiesInput) =>
  restClient.request('/api/v1/provider/matching_profile/ethnicities', { method: 'POST', data });

export type UpdateProviderCommunityExpertiesInput = {
  faith_ids: string[];
  ethnicity_ids: string[];
  community_expertise_general_ids: string[];
};

export const updateProviderCommunityExperties = async (data: UpdateProviderCommunityExpertiesInput) =>
  restClient.request('/api/v1/provider/matching_profile/community_expertise', { method: 'POST', data });

export type UpdateProviderLanguagesInput = {
  language_ids: string[];
};

export const updateProviderLanguages = async (data: UpdateProviderLanguagesInput) =>
  restClient.request('/api/v1/provider/matching_profile/languages', { method: 'POST', data });

export type UpdateProviderGendersInput = {
  gender_ids: string[];
  genders_free_text?: string[];
};

export const updateProviderGenders = async (data: UpdateProviderGendersInput) =>
  restClient.request('/api/v1/provider/matching_profile/genders', { method: 'POST', data });

export type UpdateProviderTherapeuticStylesInput = {
  therapeutic_style_ids: string[];
};

export const updateProviderTherapeuticStyles = (data: UpdateProviderTherapeuticStylesInput) =>
  restClient.request('/api/v1/provider/matching_profile/therapeutic_styles', { method: 'POST', data });


export type SaveProviderQualificationDegreeInput = {
  replaceQualificationDegreeId?: string;
  school: string;
  fieldOfStudy: string;
  yearGraduated: number;
  degree: string;
};

export const saveProviderQualificationDegree = (data: SaveProviderQualificationDegreeInput) => restClient.request('/api/v1/provider/qualification/degree', { method: 'POST', data: snakecaseKeys(data) });

export const deleteProviderQualificationDegree = (degreeId: string) => restClient.request(`/api/v1/provider/qualification/degree/${degreeId}`, { method: 'DELETE' });
